export interface AutomationBlock {
  id: string
  title: string | React.ReactNode
  description: string | React.ReactNode
  iconColor: string
  icon: React.ReactNode
  type: WorkflowFlowTypes
}

export interface AutomationFormData {
  isEnabled: boolean
  recipients: string[]
  additionalEmails: string[]
}

export enum WorkflowNotificationStates {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
  ON_HOLD = 'ON_HOLD',
  DETECTION_CONFIRMED = 'DETECTION_CONFIRMED',
  INCORRECT_DETECTION = 'INCORRECT_DETECTION',
  APPROVED = 'APPROVED',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  REQUESTED = 'REQUESTED',
  GRANTED = 'GRANTED',
  REJECTED = 'REJECTED',
  DECLINED = 'DECLINED',
  REVIEWED = 'REVIEWED',
  ACCESS_REVOKED = 'ACCESS_REVOKED',
  ACCESS_REVOKED_FAILED = 'ACCESS_REVOKED_FAILED',
  FLAGGED = 'FLAGGED',
  UNREVIEWED = 'UNREVIEWED',
  REVOKE_IN_PROGRESS = 'REVOKE_IN_PROGRESS',
  ACCESS_REVOKED_BY_SYSTEM = 'ACCESS_REVOKED_BY_SYSTEM',
  ACCESS_REVOKED_BY_USER = 'ACCESS_REVOKED_BY_USER',
  DENIED = 'DENIED'
}

export enum WorkflowNotificationStatesLable {
  OPEN = 'Open',
  IN_PROGRESS = 'In progress',
  IN_REVIEW = 'In review',
  REVIEW_COMPLETED = 'Review completed',
  ON_HOLD = 'On hold',
  DETECTION_CONFIRMED = 'Detection confirmed',
  INCORRECT_DETECTION = 'Incorrect detection',
  APPROVED = 'Approved',
  AWAITING_APPROVAL = 'Awaiting approval',
  REQUESTED = 'Requested',
  GRANTED = 'Granted',
  REJECTED = 'Rejected',
  DECLINED = 'Declined',
  REVIEWED = 'Reviewed',
  UNREVIEWED = 'Unreviewed',
  ACCESS_REVOKED = 'Access revoked',
  ACCESS_REVOKED_FAILED = 'Access revoked failed',
  FLAGGED = 'Flagged',
  PARTIALLY_GRANTED = 'Partially Granted',
  REVOKE_IN_PROGRESS = 'Revoke in Progress',
  ACCESS_REVOKED_BY_SYSTEM = 'Access revoked by System',
  ACCESS_REVOKED_BY_USER = 'Access revoked by User',
  DENIED = 'Denied'
}

export const ALLOWED_RULESET_WORKFLOW_STATES = [
  'OPEN',
  'IN_PROGRESS',
  'IN_REVIEW',
  'REVIEW_COMPLETED',
  'ON_HOLD',
  'DETECTION_CONFIRMED',
  'INCORRECT_DETECTION',
  'APPROVED',
  'AWAITING_APPROVAL'
]

export enum WorkflowRecipientGroups {
  OBJECT_OWNER = 'OBJECT_OWNER',
  DATASOURCE_OWNER = 'DATASOURCE_OWNER',
  OTHERS = 'OTHERS'
}

export enum WorkflowRecipientGroupsLabel {
  OBJECT_OWNER = 'Object Owner',
  DATASOURCE_OWNER = 'Datasource Owner',
  SITE_OWNER = 'Site Owner',
  ANALYSIS_OWNER = 'Analysis Owner',
  OTHERS = 'Others'
}

export enum WorkflowActionTypes {
  RESOLVE = 'RESOLVE',
  REASSIGN = 'REASSIGN',
  MUTE = 'MUTE',
  PERMIT = 'PERMIT',
  NO_SCAN = 'NO_SCAN',
  UNMUTE = 'UNMUTE',
  DELETE_OBJECT = 'DELETE_OBJECT',
  REVOKE_ACCESS = 'REVOKE_ACCESS',
  ARCHIVE_OBJECT_DATASOURCE = 'ARCHIVE_OBJECT_DATASOURCE',
  APPLY_LEGAL_HOLD = 'APPLY_LEGAL_HOLD',
  DISABLE_ACCOUNT = 'DISABLE_ACCOUNT'
}

export enum WorkflowActionTypesLabel {
  RESOLVE = 'Resolve',
  ADD = 'Add',
  REASSIGN = 'Reassign',
  MUTE = 'Mute',
  PERMIT = 'Permit',
  NO_SCAN = 'No scan',
  UNMUTE = 'Unmute',
  DELETE_OBJECT = 'Delete object',
  REVOKE_ACCESS = 'Revoke access',
  ARCHIVE_OBJECT_DATASOURCE = 'Archive object datasource',
  APPLY_LEGAL_HOLD = 'Apply legal hold',
  CHANGE_STATE = 'Change state',
  CREATE = 'Create',
  UPDATE = 'Update',
  DELETE = 'Delete',
  DISABLE_ACCOUNT = 'Disable account'
}

export enum WorkflowFlowTypes {
  RULE_SET = 'RULE_SET',
  ALERT = 'ALERT',
  STATE_NOTIFICATION = 'STATE_NOTIFICATION',
  ACTION = 'ACTION',

  UAR_ATTESTATION = 'UAR_ATTESTATION',
  UAR_STATE_APPROVAL = 'UAR_STATE_APPROVAL',
  UAR_REPORT_GENERATION = 'UAR_REPORT_GENERATION',
  UAR_SCAN_COMPLETE_NOTIFICATION = 'UAR_SCAN_COMPLETE_NOTIFICATION'
}

export enum WorkflowStateNotificationTypes {
  STATE_NOTIFICATION = 'STATE_NOTIFICATION',
  STATE_APPROVALS = 'STATE_APPROVALS'
}

export type TagColor =
  | 'success'
  | 'error'
  | 'caution'
  | 'cyan'
  | 'lavender'
  | 'thistle'
  | 'information'
  | 'tea-rose'
  | 'cream'
  | 'palatinate'

// ------------------------------------------------------------
export interface WorkflowGenericFormData {
  id: string
  isEnabled: boolean
  recipients: WorkflowRecipientGroups[]
  additionalNotificationRecipients: string[]
}

export interface WorkflowCronConfigFormData {
  cronExpression: string
}

export type WorkflowRuleSetFormData = WorkflowGenericFormData

export type WorkflowAlertFormData = WorkflowGenericFormData

export type WorkflowStateNotificationBlock = {
  id: string
  recipients: WorkflowRecipientGroups[]
  additionalNotificationRecipients: string[]
  states: WorkflowNotificationStates[]
}

export type WorkflowStateNotificationsFormData = {
  id: string
  [WorkflowStateNotificationTypes.STATE_APPROVALS]: WorkflowStateNotificationBlock
  [WorkflowStateNotificationTypes.STATE_NOTIFICATION]: WorkflowStateNotificationBlock
  cronExpression: string
}

export type WorkflowActionBlock = {
  id: string
  notificationsEnabled: boolean
  notificationRecipientGroups: WorkflowRecipientGroups[]
  additionalNotificationRecipients: string[]
  approvalEnabled: boolean
  approverGroups: WorkflowRecipientGroups[]
  additionalApproverRecipients: string[]
  actionType: WorkflowActionTypes
}

export type WorkflowActionsFormData = {
  id: string
  cronExpression: string
  actions: WorkflowActionBlock[]
}

// UAR
export enum WorkflowUarRecipientGroups {
  SITE_OWNER = 'SITE_OWNER',
  ANALYSIS_OWNER = 'ANALYSIS_OWNER',
  DATASOURCE_OWNER = 'DATASOURCE_OWNER'
}
export type WorkflowScanCompleteNotificationFormData = {
  id: string
  enabled: boolean
  recipientGroups: WorkflowUarRecipientGroups[]
  additionalRecipients: string[]
}

export enum WorkflowAttestationTypes {
  SELF = 'SELF',
  MANAGERS = 'DIRECT_MANAGERS',
  REVIEWERS = 'DESIGNATED_REVIEWERS'
}

export type WorkflowAttestationFormData = {
  id: string
  attestorType: WorkflowAttestationTypes
  designatedReviewers: string[]
}

export type WorkflowStateApprovalFormData = {
  id: string
  enabled: boolean
  approverGroups: WorkflowUarRecipientGroups[]
  additionalApprovers: []
  states: WorkflowFlowTypes[]
}

export type WorkflowReportGenerationFormData = {
  id: string
  enabled: boolean
  recipientGroups: WorkflowUarRecipientGroups[]
  additionalRecipients: string[]
}

export type WorkflowUARFormData = {
  [WorkflowFlowTypes.UAR_SCAN_COMPLETE_NOTIFICATION]: WorkflowScanCompleteNotificationFormData
  [WorkflowFlowTypes.UAR_ATTESTATION]: WorkflowAttestationFormData
  [WorkflowFlowTypes.UAR_STATE_APPROVAL]: WorkflowStateApprovalFormData
  [WorkflowFlowTypes.UAR_REPORT_GENERATION]: WorkflowReportGenerationFormData
}

export type WorkflowPolicyFormData = {
  [WorkflowFlowTypes.RULE_SET]: WorkflowRuleSetFormData
  [WorkflowFlowTypes.ALERT]: WorkflowAlertFormData
  [WorkflowFlowTypes.STATE_NOTIFICATION]: WorkflowStateNotificationsFormData
  [WorkflowFlowTypes.ACTION]: WorkflowActionsFormData
}

export type WorkflowFormData = WorkflowPolicyFormData | WorkflowUARFormData
